import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
    Tag, Typography, Button, Modal, Card, Divider, message,
    Descriptions, Input, Space, Col, Select, Pagination, Alert
} from "antd";
import DataTable from "../../component/ui/DataTable";
import Breadcrumbs from "../../component/Breadcrumbs";
import { ReloadOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { get, put } from "../../service/base";
import EmptyState from "../../util/EmptyState";
import { ResetButton } from "../../component/ui/ActionButtons";
import TableActions from "../../component/ui/TableActions";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import DateRangePicker from "../../component/ui/DateRangePicker";
import QuickFilter from "../../component/ui/QuickFilter";
import dayjs from "dayjs";
import { getOrderRowClass } from "../../util/statusHighlight";
import { useAdminTable } from "../hooks/useAdminTable";

const { Title, Text } = Typography;

const AdminCancellations = () => {
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [adminNote, setAdminNote] = useState("");
    const [isRefundConfirmOpen, setIsRefundConfirmOpen] = useState(false);

    const fetchRequests = async (params) => {
        const queryParams = {
            limit: params.limit,
            page: params.page,
            type: "cancel",   // ← Only fetch cancellations
        };
        if (params.search) queryParams.search = params.search;
        const queryString = new URLSearchParams(queryParams).toString();
        const res = await get(`/Orders/returns/admin-all?${queryString}`);
        return { data: res?.data || [], total: res?.total || 0 };
    };

    const {
        data: requests,
        total: totalRequests,
        loading,
        params,
        setParams,
        handleSearch,
        refresh
    } = useAdminTable(fetchRequests, "admin_cancellations", { page: 1, limit: 10, dateRange: null });

    const location = useLocation();

    const filteredRequests = useMemo(() => {
        let filtered = requests;
        if (params.dateRange && params.dateRange[0] && params.dateRange[1]) {
            const start = params.dateRange[0].startOf("day");
            const end = params.dateRange[1].endOf("day");
            filtered = filtered.filter(r => {
                const created = dayjs(r.createdAt);
                return created.isAfter(start) && created.isBefore(end);
            });
        }
        return filtered;
    }, [requests, params.dateRange]);

    useEffect(() => {
        if (location.state?.highlightOrderId) {
            setParams(prev => ({ ...prev, search: location.state.highlightOrderId, page: 1 }));
        }
    }, [location.state?.highlightOrderId]);

    const handleViewDetails = (req) => {
        setSelectedRequest(req);
        setAdminNote("");
        setIsModalOpen(true);
    };

    // Admin processes refund for online payment cancel
    const handleProcessRefund = async () => {
        if (!selectedRequest) return;
        try {
            setActionLoading(true);
            await put(`/Orders/returns/admin/${selectedRequest._id}/refund`, { stage: "completed" });
            message.success("Refund processed and credited to customer wallet successfully.");
            setIsRefundConfirmOpen(false);
            setIsModalOpen(false);
            refresh();
        } catch (error) {
            message.error(error.message || "Failed to process refund");
        } finally {
            setActionLoading(false);
        }
    };

    // ─── Helper Functions ─────────────────────────────────────────────────────
    const formatINR = (val) =>
        val != null
            ? `₹${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : "—";

    const formatStatus = (s) => {
        if (!s) return "—";
        return s.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
    };

    const isOnlinePayment = (record) => {
        const method = (record?.order?.paymentMethod || "").toUpperCase();
        return !["COD", "CASH_ON_DELIVERY"].includes(method);
    };

    const needsRefund = (record) => {
        const status = (record?.status || "").toLowerCase();
        return isOnlinePayment(record) && ["refund_pending", "return_requested", "requested"].includes(status);
    };

    const getItems = (record) => {
        if (record?.items?.length) return record.items;
        if (record?.returnItems?.length) return record.returnItems;
        return [];
    };

    const getCancelReason = (record) => {
        // Priority: adminComment > sellerComment > item returnReason > failureReason > timeline note
        if (record?.adminComment) return record.adminComment;
        if (record?.sellerComment) return record.sellerComment;
        const items = getItems(record);
        const reasons = items.map(i => i?.returnReason).filter(Boolean);
        if (reasons.length) return reasons[0];
        if (record?.failureReason) return record.failureReason;
        // Fallback: latest timeline note
        const notes = (record?.returnTimeline || [])
            .filter(t => t?.note && t.note.trim()).map(t => t.note);
        if (notes.length) return notes[notes.length - 1];
        return "—";
    };

    const getItemRefundAmount = (record) => {
        if (!record?._item) return 0;
        const items = getItems(record);

        // Calculate total product subtotal amount for all items in this request
        const totalProductAmount = items.reduce((acc, item) => {
            const qty = item?.returnQty ?? item?.quantity ?? 1;
            return acc + ((item.price || 0) * qty);
        }, 0);

        const currentQty = record._item?.returnQty ?? record._item?.quantity ?? 1;
        const currentItemAmount = (record._item.price || 0) * currentQty;

        if (totalProductAmount <= 0) return 0;

        // Proportional distribution of the total refundAmount
        const propRefund = (record.refundAmount || 0) * (currentItemAmount / totalProductAmount);
        return propRefund;
    };

    const getProductName = (item) => item?.productName || item?.name || "—";

    const getProductId = (item) => {
        const prod = item?.productId || item?.product;
        if (!prod) return "—";
        const id = prod?._id || prod;
        return id ? String(id).slice(-8).toUpperCase() : "—";
    };

    const getQty = (item) => item?.returnQty ?? item?.quantity ?? item?.orderedQty ?? 0;

    const getVariant = (item, record) => {
        // 1. Direct selectedOptions on returnItem
        let opts = item?.selectedOptions;

        // 2. Match via order.items using product reference
        let orderItem = null;
        if (record?.order?.items?.length) {
            const pId = String(
                item?.productId?._id || item?.productId ||
                item?.product?._id || item?.product || ''
            );

            if (pId) {
                orderItem = record.order.items.find(oi => {
                    const oiId = String(oi?.product?._id || oi?.product || '');
                    return oiId === pId;
                });
                if (orderItem && !opts) opts = orderItem.selectedOptions;
            }
        }

        if (opts && typeof opts === 'object') {
            const entries = Object.entries(opts).filter(([, v]) => v);
            if (entries.length) {
                return entries.map(([k, v]) => `${k}: ${v}`).join(', ');
            }
        }

        // 3. Fallback: Match item price to product variants
        const prod = item?.productId || item?.product;
        if (prod && typeof prod === 'object') {
            const targetPrice = item.price ?? orderItem?.price ?? 0;

            // 3a. Search legacy variants
            if (prod.variants?.length) {
                const matchedVar = prod.variants.find(v => (v.price || 0) === targetPrice);
                if (matchedVar) {
                    let optionName = "";
                    if (prod.options?.length) {
                        const optGroup = prod.options.find(o =>
                            o.variants?.some(ov =>
                                String(ov._id || ov) === String(matchedVar._id) ||
                                ov.value === matchedVar.value
                            )
                        );
                        if (optGroup) optionName = optGroup.name;
                    }
                    if (!optionName) {
                        optionName = prod.variantType || "Variant";
                    }
                    return `${optionName}: ${matchedVar.value}`;
                }
            }

            // 3b. Search nested options variants
            if (prod.options?.length) {
                for (const optGroup of prod.options) {
                    if (optGroup.variants?.length) {
                        const matchedVar = optGroup.variants.find(v => (v.price || 0) === targetPrice);
                        if (matchedVar) {
                            const optionName = optGroup.name || prod.variantType || "Variant";
                            return `${optionName}: ${matchedVar.value}`;
                        }
                    }
                }
            }
        }

        return "—";
    };


    const getApprovedBy = (record) => {
        const timeline = [...(record?.returnTimeline || [])].reverse();
        const recStatus = (record?.status || '').toUpperCase();
        const isRejected = recStatus.includes('REJECTED');

        // 1. Find admin who took an actionable step with a populated name
        const adminEntry = timeline.find(t => {
            const role = (t?.actorRole || '').toLowerCase();
            const tStatus = (t?.status || '').toUpperCase();
            const isAdmin = role === 'admin' || role === 'superadmin';
            const isActionable = (
                tStatus.includes('APPROVED') ||
                tStatus === 'REFUND_COMPLETED' ||
                tStatus === 'RETURN_COMPLETED' ||
                tStatus.includes('REJECTED')
            );
            return isAdmin && isActionable && t?.actorId?.firstName;
        });
        if (adminEntry?.actorId?.firstName) {
            return `${adminEntry.actorId.firstName} ${adminEntry.actorId.lastName || ''}`.trim();
        }

        // 2. Any admin timeline entry with a populated name
        const anyAdmin = timeline.find(t => {
            const role = (t?.actorRole || '').toLowerCase();
            return (role === 'admin' || role === 'superadmin') && t?.actorId?.firstName;
        });
        if (anyAdmin?.actorId?.firstName) {
            return `${anyAdmin.actorId.firstName} ${anyAdmin.actorId.lastName || ''}`.trim();
        }

        // 3. For rejections: use rejectedBy field (stored as separate ObjectId ref)
        if (isRejected && record?.rejectedBy?.firstName) {
            return `${record.rejectedBy.firstName} ${record.rejectedBy.lastName || ''}`.trim();
        }

        // 3.5 Fallback: Try to find admin name from order.statusHistory
        if (record?.order?.statusHistory) {
            const history = [...record.order.statusHistory].reverse();
            const adminHist = history.find(h => {
                const comment = (h?.comment || '').toUpperCase();
                const hasAdminKeyword = (
                    comment.includes('RETURN_APPROVED_BY_ADMIN') ||
                    comment.includes('APPROVED_BY_ADMIN') ||
                    comment.includes('RETURN_REJECTED_BY_ADMIN') ||
                    comment.includes('REJECTED_BY_ADMIN') ||
                    comment.includes('REFUND STAGE') ||
                    comment.includes('REFUND COMPLETED')
                );
                return hasAdminKeyword && h?.updatedBy?.firstName;
            });
            if (adminHist?.updatedBy?.firstName) {
                return `${adminHist.updatedBy.firstName} ${adminHist.updatedBy.lastName || ''}`.trim();
            }
        }

        // 4. Timeline has admin role entry but actorId was null (pre-fix records)
        const hasAdminNote = timeline.some(t => {
            const role = (t?.actorRole || '').toLowerCase();
            return role === 'admin' || role === 'superadmin';
        });
        if (hasAdminNote) return 'Admin';

        // 5. Infer from adminComment / sellerComment "(Admin Action)" tag
        const adminComment = record?.adminComment || '';
        const sellerComment = record?.sellerComment || '';
        if (adminComment.includes('Admin') || sellerComment.includes('Admin Action')) {
            return 'Admin';
        }

        // 6. STATUS-BASED INFERENCE for old records with NO timeline entries at all:
        const statusesThatRequireAdminApproval = [
            'RETURN_APPROVED_BY_ADMIN', 'APPROVED_BY_ADMIN',
            'RETURN_PICKUP_PENDING', 'RETURN_PICKUP_ASSIGNED',
            'RETURN_STAFF_ACCEPTED', 'RETURN_STAFF_ARRIVED_AT_CUSTOMER',
            'RETURN_PICKED_UP', 'RETURN_IN_TRANSIT', 'RETURN_IN_TRANSIT_TO_SHOP',
            'RETURN_ARRIVED_AT_SHOP', 'RETURN_DELIVERED_TO_SELLER',
            'RETURN_COMPLETED', 'WAITING_FOR_ADMIN_REFUND',
            'REFUND_PENDING', 'REFUND_PROCESSING', 'REFUND_COMPLETED',
            'RETURN_REJECTED_BY_ADMIN', 'REJECTED_BY_ADMIN',
            'RETURN_PICKUP_REJECTED',
        ];
        if (statusesThatRequireAdminApproval.includes(recStatus)) {
            return 'Admin';
        }

        return '—';
    };

    const getRemarks = (record) => {
        // Priority: adminComment > sellerComment > rejectionReason > failureReason > latest timeline note
        if (record?.adminComment) return record.adminComment;
        if (record?.sellerComment) return record.sellerComment;
        if (record?.rejectionReason) return record.rejectionReason;
        if (record?.failureReason) return record.failureReason;

        // Fallback: get last meaningful note from returnTimeline
        const notes = (record?.returnTimeline || [])
            .filter(t => t?.note && t.note.trim())
            .map(t => t.note);
        if (notes.length) return notes[notes.length - 1];

        return "—";
    };

    const STATUS_COLORS = {
        RETURN_REQUESTED: "orange",
        REQUESTED: "orange",
        REFUND_PENDING: "gold",
        REFUND_PROCESSING: "blue",
        REFUND_COMPLETED: "green",
        RETURN_COMPLETED: "green",
        REJECTED: "red",
        RETURN_REJECTED_BY_ADMIN: "red",
    };

    // ─── Flatten rows (one row per item) ────────────────────────────────────
    const flattenedRequests = useMemo(() => {
        const flatList = [];
        filteredRequests.forEach(request => {
            const items = getItems(request);
            if (items && items.length > 0) {
                items.forEach((item, index) => {
                    flatList.push({
                        ...request,
                        _item: item,
                        _itemIndex: index,
                        _uniqueKey: `${request._id}_${index}`
                    });
                });
            } else {
                flatList.push({ ...request, _item: null, _itemIndex: 0, _uniqueKey: `${request._id}_0` });
            }
        });
        return flatList;
    }, [filteredRequests]);

    // ─── Columns ─────────────────────────────────────────────────────────────
    const columns = [
        {
            title: "Cancel ID",
            dataIndex: "returnId",
            key: "returnId",
            width: 50,
            render: (id) => <span style={{ color: "#334155", fontSize: 13, fontWeight: 600 }}>{id || "—"}</span>
        },
        {
            title: "Order ID",
            key: "orderId",
            width: 130,
            render: (_, r) => <span style={{ color: "#334155", fontSize: 13, fontWeight: 600 }}>{r.order?.orderId || "—"}</span>
        },
        {
            title: "Order Date",
            key: "orderDate",
            width: 160,
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{r.order?.createdAt ? dayjs(r.order.createdAt).format("DD MMM YYYY") : "—"}</span>
        },
        {
            title: "Cancel Date",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 160,
            render: (v) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{v ? dayjs(v).format("DD MMM YYYY") : "—"}</span>
        },
        {
            title: "Customer",
            key: "customer",
            width: 190,
            render: (_, r) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{r.user?.firstName} {r.user?.lastName}</span>
                    {r.user?.phone && <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>📞 {r.user.phone}</span>}
                    {r.user?.email && <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>✉️ {r.user.email}</span>}
                </div>
            )
        },
        {
            title: "Seller / Shop Name",
            key: "shopName",
            width: 150,
            render: (_, r) => {
                const sellerName = r.shop?.createdBy
                    ? `${r.shop.createdBy.firstName} ${r.shop.createdBy.lastName || ''}`.trim()
                    : '—';
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{r.shop?.name || "—"}</span>
                        <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>👤 {sellerName}</span>
                    </div>
                );
            }
        },
        {
            title: "Product Name",
            key: "productName",
            width: 200,
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{getProductName(r._item)}</span>
        },
        {
            title: "Product ID",
            key: "productId",
            width: 120,
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{getProductId(r._item)}</span>
        },
        {
            title: "Variant",
            key: "variant",
            width: 160,
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{getVariant(r._item, r)}</span>
        },
        {
            title: "Quantity",
            key: "quantity",
            width: 90,
            align: 'center',
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{getQty(r._item)}</span>
        },
        {
            title: "Unit Price",
            key: "unitPrice",
            width: 110,
            align: 'right',
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{formatINR(r._item?.price)}</span>
        },
        {
            title: "Product Amount",
            key: "productAmount",
            width: 135,
            align: "right",
            render: (_, r) => {
                if (!r._item) return <span style={{ color: '#9ca3af', fontSize: 13, fontWeight: 600 }}>—</span>;
                return <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{formatINR((r._item.price || 0) * getQty(r._item))}</span>;
            }
        },
        {
            title: "Discount / Coupon",
            key: "discount",
            width: 140,
            align: "right",
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{formatINR(r.order?.couponDiscount ?? 0)}</span>
        },
        {
            title: "Delivery Fee",
            key: "deliveryFee",
            width: 120,
            align: "right",
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{formatINR(r.order?.deliveryCharge ?? 0)}</span>
        },
        {
            title: "Total Amount",
            key: "refundAmount",
            width: 140,
            align: "right",
            render: (_, r) => {
                const method = (r.order?.paymentMethod || "").toUpperCase();
                const isCOD = ["COD", "CASH_ON_DELIVERY"].includes(method);
                if (isCOD) return <span style={{ color: "#9ca3af", fontSize: 13, fontWeight: 600 }}>—</span>;

                const itemRefund = getItemRefundAmount(r);
                const items = getItems(r);
                const showRequestTotal = items.length > 1;

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span style={{ fontWeight: 600, color: "#334155", fontSize: 13 }}>
                            {formatINR(itemRefund)}
                        </span>
                        {showRequestTotal && (
                            <span style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                                Total: {formatINR(r.refundAmount)}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Payment Method",
            key: "paymentMethod",
            width: 140,
            render: (_, r) => {
                const method = r.order?.paymentMethod;
                const isCOD = ["COD", "CASH_ON_DELIVERY"].includes((method || "").toUpperCase());
                return (
                    <Tag color={isCOD ? "default" : "blue"} style={{ borderRadius: 6, fontSize: 12 }}>
                        {method || "—"}
                    </Tag>
                );
            }
        },
        {
            title: "Action",
            key: "action",
            width: 90,
            align: "center",
            render: (_, record) => (
                <Button
                    type="default"
                    size="small"
                    onClick={() => handleViewDetails(record)}
                    style={{ borderRadius: 4, fontWeight: 500, color: "#003a8c", borderColor: "#d9d9d9" }}
                >
                    View
                </Button>
            )
        }
    ];

    const processedColumns = columns.map(col => {
        const nowrapTitles = [
            "Cancel ID", "Order ID", "Order Date", "Cancel Date",
            "Product ID", "Quantity", "Unit Price", "Product Amount", "Discount / Coupon",
            "Delivery Fee", "Total Amount", "Payment Method"
        ];
        if (nowrapTitles.includes(col.title)) {
            return {
                ...col,
                className: `${col.className || ''} nowrap-column`.trim()
            };
        }
        return col;
    });

    const handleReset = () => {
        setParams({ page: 1, limit: 10, search: "", dateRange: null });
    };

    const fetchFilteredData = async () => flattenedRequests;

    // ─── Modal: Online refund amount to show ────────────────────────────────
    const modalRefundAmount = selectedRequest
        ? formatINR(selectedRequest.refundAmount)
        : "—";
    const modalMethod = selectedRequest?.order?.paymentMethod;
    const modalIsCOD = ["COD", "CASH_ON_DELIVERY"].includes((modalMethod || "").toUpperCase());
    const modalNeedsRefund = selectedRequest && needsRefund(selectedRequest);

    return (
        <div className="product-option-container pcu-padding-24">
            <style>{`
                .custom-table .ant-table-thead > tr > th {
                    font-size: 13px !important;
                    font-weight: 700 !important;
                    color: #1e293b !important;
                    text-transform: capitalize !important;
                    letter-spacing: normal !important;
                    opacity: 1 !important;
                }
                .custom-table .ant-table-tbody > tr > td {
                    font-family: var(--font-family-primary, "DM Sans", sans-serif) !important;
                    font-size: 13px !important;
                }
                .custom-table .ant-table-tbody > tr > td * {
                    font-family: var(--font-family-primary, "DM Sans", sans-serif) !important;
                }
                .custom-table .ant-table-tbody > tr > td .monospace-text {
                    font-family: monospace !important;
                }
                .custom-table .nowrap-column {
                    white-space: nowrap !important;
                }
            `}</style>
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="main-content-card pcu-bg-white pcu-radius-16 pcu-shadow-sm pcu-overflow-hidden">
                <div className="pcu-padding-x-24 pcu-padding-top-24 pcu-padding-bottom-16 pcu-border-bottom">
                    <div className="pcu-flex pcu-justify-between pcu-items-start">
                        <div>
                            <h1 className="pcu-font-22 pcu-font-700 pcu-color-title pcu-margin-0">
                                Cancellation Audit
                            </h1>
                            <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 13 }}>
                                All cancelled orders — online payment refunds require admin approval
                            </p>
                        </div>
                        <div className="pcu-flex pcu-gap-12 pcu-items-center">
                            <TableActions
                                onFetchFullData={fetchFilteredData}
                                columns={processedColumns}
                                fileName="cancellation_audits"
                                pageName="Cancellation Audits"
                            />
                            <ResetButton
                                icon={<ReloadOutlined />}
                                onClick={handleReset}
                                text="Refresh"
                            />
                        </div>
                    </div>
                </div>

                <div className="pcu-padding-24">
                    <FilterBar>
                        <Col xs={24} md={8}>
                            <SearchInput
                                label="Search Cancellations"
                                placeholder="Search by Cancel ID, Order ID, Customer..."
                                value={params.search}
                                onChange={(val) => handleSearch(val)}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <QuickFilter
                                value={params.dateRange ? "custom" : undefined}
                                onChange={(key, range) =>
                                    setParams(prev => ({ ...prev, dateRange: range, page: 1 }))
                                }
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <DateRangePicker
                                label="Custom Date Range"
                                value={params.dateRange}
                                onChange={(val) =>
                                    setParams(prev => ({ ...prev, dateRange: val, page: 1 }))
                                }
                            />
                        </Col>
                    </FilterBar>

                    <div className="pcu-border-top">
                        <DataTable
                            columns={processedColumns}
                            data={flattenedRequests}
                            rowKey="_uniqueKey"
                            loading={loading}
                            scroll={{ x: 'max-content' }}
                            pagination={false}
                            rowClassName={() => ""}
                            locale={{
                                emptyText: (
                                    <EmptyState
                                        title="No Cancellations Found"
                                        description="No cancelled orders found in the system."
                                        gradient="red"
                                        showAction={false}
                                        icon="inbox"
                                    />
                                )
                            }}
                        />
                    </div>

                    <div style={{ padding: "16px", display: "flex", justifyContent: "flex-end", borderTop: "1px solid #f0f0f0" }}>
                        <Pagination
                            current={params.page}
                            pageSize={params.limit}
                            total={totalRequests}
                            showSizeChanger
                            onShowSizeChange={(c, s) => setParams(prev => ({ ...prev, limit: s, page: 1 }))}
                            onChange={(p) => setParams(prev => ({ ...prev, page: p }))}
                            showTotal={(total) => `Total ${total} cancellations`}
                        />
                    </div>
                </div>
            </div>

            {/* ─── Detail Modal ─────────────────────────────────────────────────── */}
            <Modal
                title={null}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width={520}
                centered
                styles={{ body: { padding: 0 }, content: { borderRadius: 20, overflow: "hidden", padding: 0 } }}
            >
                {selectedRequest && (
                    <>
                        {/* Header */}
                        <div style={{
                            background: "linear-gradient(135deg, #1a1a2e 0%, #7f1d1d 100%)",
                            padding: "20px 24px 16px",
                            position: "relative",
                            overflow: "hidden"
                        }}>
                            <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(239,68,68,0.15)" }} />
                            <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative" }}>
                                <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(239,68,68,0.2)", border: "1px solid rgba(239,68,68,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                                    ❌
                                </div>
                                <div>
                                    <div style={{ color: "#fff", fontSize: 18, fontWeight: 700 }}>Cancellation Details</div>
                                    <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, marginTop: 2 }}>
                                        Order #{selectedRequest.order?.orderId} • {selectedRequest.returnId}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Body */}
                        <div style={{ padding: "16px", maxHeight: "65vh", overflowY: "auto", background: "#f8f9fc" }}>

                            {/* Refund Pending Banner */}
                            {modalNeedsRefund && (
                                <Alert
                                    icon={<ExclamationCircleOutlined />}
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: 16, borderRadius: 10 }}
                                    message="Refund Approval Required"
                                    description={
                                        <span>
                                            This is an <strong>online payment</strong> cancellation. Refund amount{" "}
                                            <strong>{modalRefundAmount}</strong> is pending admin approval.
                                            Customer has been notified that refund will arrive in{" "}
                                            <strong>3–4 business days</strong>.
                                        </span>
                                    }
                                />
                            )}

                            {/* COD Info */}
                            {modalIsCOD && (
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16, borderRadius: 10 }}
                                    message="COD Order — No Refund Required"
                                    description="Cash on Delivery order. No online payment was made, so no refund is needed."
                                />
                            )}

                            <Descriptions bordered size="small" column={1} style={{ background: "#fff", borderRadius: 12, overflow: "hidden", marginBottom: 16, border: "1px solid #e5e7eb" }}>
                                <Descriptions.Item label="Customer">
                                    <div style={{ fontWeight: 600 }}>{selectedRequest.user?.firstName} {selectedRequest.user?.lastName}</div>
                                    {selectedRequest.user?.phone && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>📞 {selectedRequest.user.phone}</div>}
                                    {selectedRequest.user?.email && <div style={{ fontSize: 11, color: "#6b7280" }}>✉️ {selectedRequest.user.email}</div>}
                                </Descriptions.Item>
                                <Descriptions.Item label="Shop">
                                    <div style={{ fontWeight: 600 }}>{selectedRequest.shop?.name || "—"}</div>
                                </Descriptions.Item>
                                <Descriptions.Item label="Payment Method">
                                    <Tag color={modalIsCOD ? "default" : "blue"} style={{ borderRadius: 6 }}>
                                        {modalMethod || "—"}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Refund Amount">
                                    <span style={{ fontWeight: 700, color: "#16a34a", fontSize: 15 }}>
                                        {modalIsCOD ? "—" : modalRefundAmount}
                                    </span>
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag color={modalNeedsRefund ? "gold" : "green"} style={{ borderRadius: 6, fontWeight: 600 }}>
                                        {modalNeedsRefund ? "Refund Pending" : formatStatus(selectedRequest.status)}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Cancel Reason">
                                    <span style={{ fontSize: 13 }}>{getCancelReason(selectedRequest)}</span>
                                </Descriptions.Item>
                                <Descriptions.Item label="Cancelled On">
                                    {selectedRequest.createdAt ? dayjs(selectedRequest.createdAt).format("DD MMM YYYY • hh:mm A") : "—"}
                                </Descriptions.Item>
                            </Descriptions>

                            {/* Cancelled Items List */}
                            <Title level={5} style={{ fontSize: 14, marginBottom: 10 }}>Cancelled Items</Title>
                            {getItems(selectedRequest).map((item, idx) => (
                                <div key={idx} style={{
                                    marginBottom: 8, background: "#ffffff", border: "1px solid #e5e7eb",
                                    borderRadius: 10, padding: "10px 12px"
                                }}>
                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{item.productName || item.name || "—"}</div>
                                    {getVariant(item, selectedRequest) !== "—" && (
                                        <div style={{ fontSize: 11, color: "#ef4444", marginTop: 2, fontWeight: 500 }}>
                                            Variant: {getVariant(item, selectedRequest)}
                                        </div>
                                    )}
                                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                                        Qty: {item.returnQty || item.quantity || 1}{" "}
                                        <span style={{ color: "#e5e7eb", margin: "0 6px" }}>|</span>
                                        <span style={{ color: "#16a34a", fontWeight: 600 }}>
                                            {formatINR((item.price || 0) * (item.returnQty || item.quantity || 1))}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        {modalNeedsRefund && (
                            <div style={{ padding: "12px 16px", borderTop: "1px solid #f0f0f0", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                                <Button onClick={() => setIsModalOpen(false)}>
                                    Close
                                </Button>
                                <Button
                                    type="primary"
                                    style={{ background: "#16a34a", borderColor: "#16a34a", fontWeight: 600 }}
                                    onClick={() => setIsRefundConfirmOpen(true)}
                                >
                                    ✅ Process Refund — {modalRefundAmount}
                                </Button>
                            </div>
                        )}
                        {!modalNeedsRefund && (
                            <div style={{ padding: "12px 16px", borderTop: "1px solid #f0f0f0", background: "#fff", display: "flex", justifyContent: "flex-end" }}>
                                <Button onClick={() => setIsModalOpen(false)}>Close</Button>
                            </div>
                        )}
                    </>
                )}
            </Modal>

            {/* ─── Refund Confirmation Modal ────────────────────────────────────── */}
            <Modal
                title="Confirm Refund Processing"
                open={isRefundConfirmOpen}
                onCancel={() => setIsRefundConfirmOpen(false)}
                onOk={handleProcessRefund}
                okText="Yes, Process Refund"
                cancelText="Cancel"
                okButtonProps={{ loading: actionLoading, style: { background: "#16a34a", borderColor: "#16a34a" } }}
                centered
            >
                <p>
                    Are you sure you want to process a refund of{" "}
                    <strong>{modalRefundAmount}</strong> for order{" "}
                    <strong>#{selectedRequest?.order?.orderId}</strong>?
                </p>
                <p style={{ color: "#6b7280", fontSize: 13 }}>
                    The amount will be credited to the customer's wallet immediately.
                </p>
            </Modal>
        </div>
    );
};

export default AdminCancellations;
